import React, { FC, useState, useRef, ReactNode, useEffect } from 'react';
import styled from '@emotion/styled';
import {
  TablesAndFloorPlansQuery_venue_floorPlans_tables as TableType,
  TablesAndFloorPlansQuery_venue_floorPlans_markers as MarkerType,
  TablesAndFloorPlansQuery_venue_floorPlans_structures as StructureType,
} from '~Scenes/Handy/__queries__';
import { TableComponentProps, TableKey } from '../../types';
import DragChequeHandler from './DragChequeHandler';
import Marker from '~Components/Marker';
import Structure from '~Components/Structure';
import { HANDY_SCALE_FACTOR } from './constants';

const Wrapper = styled.div`
  display: flex;
  flex-flow: row wrap;
  min-height: calc(100vh - 86px); /* 86px is the height of the top bar */
  height: calc(100% + 150px);
  width: calc(100% + 150px);
  position: relative;
  overflow: auto; /* Enable scrolling */
  box-sizing: border-box;
  touch-action: none; /* Disable default touch scrolling */

  transition: box-shadow 0.2s ease-in-out;
`;

/* Zoom and drag container */
const ZoomWrapper = styled.div`
  width: calc(100%);
  height: calc(100%);
  position: relative;
`;

/* Re-export for backward compatibility */
export { HANDY_SCALE_FACTOR } from './constants';

export const InnerWrapper = styled.div<{
  posY: number;
  posX: number;
}>`
  position: absolute;
  top: ${(props) => props.posY * HANDY_SCALE_FACTOR}px; /* Adjust position based on screen size */
  left: ${(props) => props.posX * HANDY_SCALE_FACTOR}px; /* Adjust position based on screen size */
`;

interface Props {
  tables: TableType[];
  markers: MarkerType[];
  structures: StructureType[];
  tableComponent: ({ table, keyType, isMapView }: TableComponentProps) => ReactNode;
  chequeIsDragging: boolean;
}

/* Main component displaying the tables */
const TableMap: FC<Props> = ({
  tables,
  markers,
  structures,
  tableComponent,
  chequeIsDragging,
}) => {
  const scaleRef = useRef<number>(1); /* Zoom level */
  const panOffsetRef = useRef<{ x: number; y: number }>({
    x: 0,
    y: 0,
  }); /* Current pan position (i.e drag position) */

  const zoomWrapperRef = useRef<HTMLDivElement>(null); /* Reference to ZoomWrapper */

  const wrapperRef = useRef<HTMLDivElement>(null);
  const lastTouchPosRef = useRef<{ x: number; y: number } | null>(null);
  const lastTouchDistanceRef = useRef<number | null>(
    null,
  ); /* Reference to last touch distance for zooming */

  /* Calculate the bounding box around all tables */
  const minX = Math.min(...tables.map((table) => table.posX));
  const maxX = Math.max(...tables.map((table) => table.posX));
  const minY = Math.min(...tables.map((table) => table.posY));
  const maxY = Math.max(...tables.map((table) => table.posY));

  /* Margins added to allow a little padding */
  const EXTRA_MARGIN = 100;

  /* Additional space for left and top to make the map more "draggable" */
  const EXTRA_DRAG_SPACE = 100;

  /* Calculate the width and height of the map */
  const mapWidth = maxX - minX + EXTRA_MARGIN * 2;
  const mapHeight = maxY - minY + EXTRA_MARGIN * 2;

  /* Adjust panning boundaries based on scale and grid size */
  const calculateBoundaries = (scale: number) => {
    const wrapper = wrapperRef.current!;
    const wrapperRect = wrapper.getBoundingClientRect();

    /* Calculate total width and height of the table grid with scaling */
    const totalWidth = (maxX - minX) * scale + EXTRA_MARGIN * 2;
    const totalHeight = (maxY - minY) * scale + EXTRA_MARGIN * 2;

    /* Allow more dynamic drag space based on how zoomed out the user is */
    const dynamicDragSpaceX = EXTRA_DRAG_SPACE * (2 - scale);
    const dynamicDragSpaceY = EXTRA_DRAG_SPACE * (3 - scale);

    /* Ensure user can pan fully but always keep at least one table visible */
    const maxPanX = dynamicDragSpaceX;
    const minPanX = Math.min(
      dynamicDragSpaceX,
      wrapperRect.width - totalWidth - dynamicDragSpaceX,
    );

    const maxPanY = dynamicDragSpaceY;
    const minPanY = Math.min(
      dynamicDragSpaceY,
      wrapperRect.height - totalHeight - dynamicDragSpaceY,
    );

    return {
      minX: minPanX,
      maxX: maxPanX,
      minY: minPanY,
      maxY: maxPanY,
    };
  };

  /* Function to constrain panOffset based on boundaries */
  const constrainPanOffset = (offset: { x: number; y: number }, boundaries: any) => {
    const constrainedOffset = {
      x: Math.min(Math.max(offset.x, boundaries.minX), boundaries.maxX),
      y: Math.min(Math.max(offset.y, boundaries.minY), boundaries.maxY),
    };

    return constrainedOffset;
  };

  /* Function to handle touch start events */
  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      /* For two-finger pinch (zoom) */
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY,
      );
      lastTouchDistanceRef.current = distance; /* Store the initial distance */
    } else if (e.touches.length === 1) {
      /* For single-finger drag */
      const touch = e.touches[0];
      lastTouchPosRef.current = { x: touch.clientX, y: touch.clientY };
    }
  };

  /* Function to handle touch move (drag and zoom) */
  const handleTouchMove = (e: React.TouchEvent) => {
    e.preventDefault();

    const boundaries = calculateBoundaries(scaleRef.current);

    if (e.touches.length === 2) {
      /* Handle pinch zoom */
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY,
      );

      if (lastTouchDistanceRef.current !== null) {
        const scaleChange = distance / lastTouchDistanceRef.current;
        const newScale = Math.max(
          0.5,
          Math.min(3, scaleRef.current * scaleChange),
        ); /* Ensure the scale stays within limits */

        const midX = (touch1.clientX + touch2.clientX) / 2;
        const midY = (touch1.clientY + touch2.clientY) / 2;

        const wrapper = wrapperRef.current!;
        const wrapperRect = wrapper.getBoundingClientRect();

        /* Adjust the panOffset to keep the zoom point consistent */
        const deltaX = (midX - wrapperRect.left) * (newScale - scaleRef.current);
        const deltaY = (midY - wrapperRect.top) * (newScale - scaleRef.current);

        const newPanX = panOffsetRef.current.x - deltaX;
        const newPanY = panOffsetRef.current.y - deltaY;

        scaleRef.current = newScale;
        panOffsetRef.current = constrainPanOffset({ x: newPanX, y: newPanY }, boundaries);

        lastTouchDistanceRef.current = distance;

        updateZoomWrapper();
      }
    } else if (e.touches.length === 1) {
      /* Handle drag */
      const touch = e.touches[0];
      if (lastTouchPosRef.current) {
        const deltaX = touch.clientX - lastTouchPosRef.current.x;
        const deltaY = touch.clientY - lastTouchPosRef.current.y;

        const newOffset = {
          x: panOffsetRef.current.x + deltaX,
          y: panOffsetRef.current.y + deltaY,
        };

        panOffsetRef.current = constrainPanOffset(newOffset, boundaries);

        lastTouchPosRef.current = { x: touch.clientX, y: touch.clientY };

        updateZoomWrapper();
      }
    }
  };

  /* Function to directly update the ZoomWrapper's transform style */
  const updateZoomWrapper = () => {
    if (zoomWrapperRef.current) {
      zoomWrapperRef.current.style.transform = `translate(${panOffsetRef.current.x}px, ${panOffsetRef.current.y}px) scale(${scaleRef.current})`;
    }
  };

  /* Reset touch data on end */
  const handleTouchEnd = () => {
    lastTouchDistanceRef.current = null;
    lastTouchPosRef.current = null;
  };

  /* Adjust the zoom level when dragging a cheque */
  useEffect(() => {
    if (chequeIsDragging) {
      scaleRef.current = 0.9; // Zoom out to make it easier to drag cheques
    } else {
      scaleRef.current = 1; // Reset the zoom level
    }
    updateZoomWrapper();
  }, [chequeIsDragging]);

  if (chequeIsDragging) {
    return (
      <DragChequeHandler
        tables={tables}
        markers={markers}
        structures={structures}
        tableComponent={tableComponent}
        mapWidth={mapWidth}
        mapHeight={mapHeight}
      />
    );
  }

  return (
    <Wrapper
      id="tablemap-wrapper"
      ref={wrapperRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}>
      <ZoomWrapper ref={zoomWrapperRef}>
        {tables.map((table) => (
          <InnerWrapper key={table.id} posX={table.posX} posY={table.posY}>
            {tableComponent({
              table,
              keyType: TableKey['floor_plan'],
              isMapView: true,
            })}
          </InnerWrapper>
        ))}
        {markers.map((marker) => (
          <InnerWrapper key={marker.id} posX={marker.posX} posY={marker.posY}>
            <Marker icon={marker.icon} size={marker.size} isHandy />
          </InnerWrapper>
        ))}
        {structures.map((structure) => {
          return (
            <Structure
              key={structure.id}
              posX={structure.posX}
              posY={structure.posY}
              width={structure.width}
              height={structure.height}
              rotation={(structure as any).rotation || 0}
              isHandy
            />
          );
        })}
      </ZoomWrapper>
    </Wrapper>
  );
};

export default TableMap;
